import { useMemo } from 'react';
import _ from 'lodash';
import { gql } from '@apollo/client';
import MetricsSummary from '../components/economics/MetricsSummary';
import { useDataTransforms, useFarmName, useFilterYears } from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { useUnits } from '../contexts/UnitContext';
import { max } from '../utils/aggregators';
import { asQuantityWithUnit } from '../utils/formatters';
import ItemsTable from '../components/tables/ItemsTable';
import { colorForCrop } from '../utils/colors';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import CARBON_METRICS from '../constants/carbonMetrics';
import { metricsQueryString } from '../utils/utils';
import Content from '../components/layout/Content';
import { CropDisplayFragment } from '../queries';
import { DBH_UNITS } from '../constants/units';
import { getCarbonData } from '../utils/transforms';

const ALL_CROPS = 'Full Site';
const QUERY_NAME = 'CarbonEconQuery';
const FRAGMENT_NAME = 'CarbonEconFragment';

const CarbonFragment = gql`
  ${CropDisplayFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    ecologicalSummary {
      carbonTonsByYear
    }
    cropAggregates {
      crop {
        ...CropDisplayFields
        carbon {
          ${metricsQueryString(CARBON_METRICS)}
        }
      }
      ecologicalSummary {
        carbonTonsByYear
      }
    }
  }
`;

const toSummaryData = ({ cropAggregates, ecologicalSummary }) => ({
  ..._.fromPairs(
    cropAggregates.map(({ crop, carbonTonsByYear }) => [
      crop.name,
      carbonTonsByYear,
    ]),
  ),
  [ALL_CROPS]: ecologicalSummary.carbonTonsByYear,
});

const getSummaryMetrics = ({ cropAggregates }) => {
  return [
    { crop: { name: ALL_CROPS, isAggregate: true } },
    ...cropAggregates,
  ].map(({ crop }) => ({
    displayName: crop.name,
    accessor: crop.name,
    isAggregate: crop.isAggregate,
    aggregator: max,
    color: colorForCrop(crop),
    defaultShown: true,
    format: asQuantityWithUnit('tons'),
    info: `(Max CO2)`,
    yAxisId: 'tons',
    unit: 'tons',
  }));
};

const getItems = (cropAggregate) => [cropAggregate];

const CarbonView = () => {
  const filterYears = useFilterYears();
  const farmName = useFarmName();

  const { msg, data } = useFarmOrSectionGroupData(
    CarbonFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
  );

  const dbhUnits = useUnits(DBH_UNITS);

  const convertedData = useMemo(() => data && getCarbonData(dbhUnits)(data), [
    data,
    dbhUnits,
  ]);

  const [summaryData, summaryMetrics] = useDataTransforms(convertedData, [
    toSummaryData,
    getSummaryMetrics,
  ]);

  return (
    <>
      <MetricsHeader showCumulativeYearSelect showAnnualSingleYearSelect>
        Carbon
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={summaryMetrics}
            data={summaryData}
            arrayTransform={filterYears}
          />
        </SummarySection>
        <DetailsSection>
          <ItemsTable
            data={convertedData}
            cols={CARBON_METRICS}
            msg={msg}
            getItems={getItems}
            byCrop
            tableHeader={<h3>Carbon Metrics</h3>}
            csvFileName={['Carbon', farmName].join(' - ')}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default CarbonView;
